/**
 * The report's tables that are tied to CSRD: whether each still agrees with it,
 * and the one action that brings it back in line.
 *
 * **Detected, never applied on its own.** Opening a report reads each linked
 * table from CSRD once and compares it with what the report holds, so a bar can
 * say the figures have moved. Nothing is written until somebody asks: figures
 * changing under a report that is being reviewed, validated or audited are
 * exactly what that process exists to prevent. A report that cannot be edited
 * in its present state is not even checked, and is refused if asked.
 *
 * CSRD is reached one table at a time, one after another. It does not survive
 * overlapping requests from one person (see `infrastructure/csrd/client.py`),
 * and a report has a handful of linked tables, not hundreds.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { csrdSetCurrentReporting, csrdTablePull, type CsrdTablePull } from '@/api/csrd'
import { ApiError, NetworkError } from '@/api/http'
import { isAttached } from '@/domain/csrdReportings'
import type { CsrdLink, TableSection } from '@/domain/section'
import { applyPull, compareToCsrd } from '@/features/csrdLink/refresh'
import { currentLocale, t } from '@/i18n'
import { useReportStore } from '@/stores/report'

/** The most tables checked on opening. A report with more is checked as far as this. */
const MAX_CHECKED = 12

export type LinkPhase = 'checking' | 'current' | 'stale' | 'refreshing' | 'failed'

/** What was done by the last refresh, for the line that says so. */
export interface RefreshResult {
  readonly changed: number
  readonly reshaped: boolean
  readonly at: string
}

export interface LinkState {
  readonly phase: LinkPhase
  /** How many cells differ from CSRD, while stale. */
  readonly changed: number
  readonly reshaped: boolean
  readonly problem: string | null
  /**
   * The reporting CSRD has to be on for this table to be read, when it is on
   * another. Set for the document's principal reporting only: a secondary one
   * is moved onto by the server.
   */
  readonly blocked: string | null
  readonly last: RefreshResult | null
}

const IDLE: LinkState = {
  phase: 'current',
  changed: 0,
  reshaped: false,
  problem: null,
  blocked: null,
  last: null,
}

export const useCsrdLinksStore = defineStore('csrdLinks', () => {
  const report = useReportStore()

  const states = ref<Record<string, LinkState>>({})
  /** The bar was put away for this opening of the report. */
  const dismissed = ref(false)

  /** Every table of the report that is tied to CSRD, in document order. */
  const linked = computed<TableSection[]>(() =>
    report.outline.flatMap((row) =>
      row.section.kind === 'table' && row.section.csrd ? [row.section] : [],
    ),
  )

  function stateOf(id: string): LinkState {
    return states.value[id] ?? IDLE
  }

  function set(id: string, next: Partial<LinkState>): void {
    states.value = { ...states.value, [id]: { ...stateOf(id), ...next } }
  }

  /**
   * The reporting CSRD must be moved onto before the report's tables can be
   * read: the principal one, which is never moved onto without being asked.
   */
  const blockedOn = computed(() => {
    for (const table of linked.value) {
      const code = stateOf(table.id).blocked
      if (code) return code
    }
    return null
  })

  /** Said once for the whole report, whatever the number of tables behind it. */
  const moveProblem = ref<string | null>(null)

  /** The tables whose figures differ from CSRD's. */
  const stale = computed(() => linked.value.filter((table) => stateOf(table.id).phase === 'stale'))

  /** The oldest reading among them: how far behind the report is. */
  const oldestReading = computed(() => {
    const dates = stale.value.map((table) => table.csrd?.pulledAt ?? '').filter(Boolean)
    return dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : ''
  })

  const showBar = computed(
    () =>
      !dismissed.value && report.editable && (stale.value.length > 0 || blockedOn.value !== null),
  )

  function describe(error: unknown, link: CsrdLink): string {
    if (error instanceof ApiError) {
      if (error.code === 'csrd_other_reporting') {
        return t('csrdLink.errors.otherReporting', { code: link.reportingCode })
      }
      if (error.code === 'csrd_dev_token') return t('importing.csrd.devToken')
      if (error.code === 'csrd_no_client') return t('importing.csrd.noClient')
      if (error.isForbidden) return t('importing.csrd.forbidden')
      if (error.isNotFound) return t('csrdLink.errors.gone')
      if (error.code === 'csrd_error') return t('importing.csrd.failed')
    }
    if (error instanceof NetworkError) return t('common.unreachable')
    return t('importing.csrd.unreachable')
  }

  /**
   * The table as CSRD holds it now.
   *
   * With the document's principal reporting named, so that a table of a
   * secondary reporting can be read by moving onto it and back.
   */
  function read(link: CsrdLink): Promise<CsrdTablePull> {
    return csrdTablePull(
      {
        entityId: link.entityId,
        roleTypeId: link.roleTypeId,
        roleCode: link.roleCode,
        tableIndex: link.tableIndex,
        reportingCode: link.reportingCode,
        principalCode: report.principal?.code ?? '',
      },
      link.language || currentLocale(),
    )
  }

  /** Whether a table can be read at all: its reporting must still be one of the document's. */
  function detached(table: TableSection): boolean {
    return Boolean(table.csrd) && !isAttached(table.csrd as CsrdLink, report.reportings)
  }

  /** Why a table failed, and whether the cure is to move CSRD onto its reporting. */
  function failure(error: unknown, link: CsrdLink): Partial<LinkState> {
    const wrong = error instanceof ApiError && error.code === 'csrd_other_reporting'
    return {
      phase: 'failed',
      problem: describe(error, link),
      blocked: wrong ? link.reportingCode : null,
    }
  }

  /** Whether one table still agrees with CSRD. Changes nothing in the report. */
  async function check(id: string): Promise<void> {
    const table = linked.value.find((one) => one.id === id)
    if (!table?.csrd) return

    if (detached(table)) {
      set(id, {
        phase: 'failed',
        problem: t('csrdLink.errors.detached', { code: table.csrd.reportingCode }),
      })
      return
    }

    set(id, { phase: 'checking', problem: null, blocked: null })
    try {
      const comparison = compareToCsrd(table, await read(table.csrd))
      const differs = comparison.changed > 0 || comparison.reshaped
      set(id, {
        phase: differs ? 'stale' : 'current',
        changed: comparison.changed,
        reshaped: comparison.reshaped,
      })
    } catch (error) {
      set(id, failure(error, table.csrd))
    }
  }

  /**
   * Every linked table, once, when the report opens.
   *
   * Skipped for a report nobody can edit right now: there is nothing to
   * refresh, and CSRD need not be asked.
   */
  async function checkAll(): Promise<void> {
    if (!report.editable) return
    for (const table of linked.value.slice(0, MAX_CHECKED)) await check(table.id)
  }

  /** Read the table again and bring the report's in line, saying how much moved. */
  async function refresh(id: string): Promise<void> {
    const table = linked.value.find((one) => one.id === id)
    if (!table?.csrd) return

    if (!report.editable) {
      set(id, { phase: 'failed', problem: t('csrdLink.errors.notEditable') })
      return
    }

    if (detached(table)) {
      set(id, {
        phase: 'failed',
        problem: t('csrdLink.errors.detached', { code: table.csrd.reportingCode }),
      })
      return
    }

    set(id, { phase: 'refreshing', problem: null, blocked: null })
    try {
      const pull = await read(table.csrd)
      const comparison = compareToCsrd(table, pull)
      const at = new Date().toISOString()

      // Nothing moved: the report is left as it is, and no undo step is spent on it.
      if (comparison.changed > 0 || comparison.reshaped) report.update(applyPull(table, pull))

      set(id, {
        phase: 'current',
        changed: 0,
        reshaped: false,
        last: { changed: comparison.changed, reshaped: comparison.reshaped, at },
      })
    } catch (error) {
      set(id, failure(error, table.csrd))
    }
  }

  /**
   * Move the person onto the reporting a table needs, in CSRD, and look again.
   *
   * Done only when they ask, from the button that says so: it changes what
   * CSRD's own screens open on for them.
   */
  async function moveCsrdTo(code: string): Promise<void> {
    const wanted = report.reportings.find((one) => one.code === code)
    if (!wanted) return

    moveProblem.value = null
    try {
      await csrdSetCurrentReporting(wanted.id)
    } catch (error) {
      moveProblem.value =
        error instanceof ApiError ? error.message : t('importing.csrd.unreachable')
      return
    }
    await checkAll()
  }

  async function refreshAll(): Promise<void> {
    for (const table of [...stale.value]) await refresh(table.id)
  }

  /** A different report, or the same one reopened: nothing carried over. */
  function reset(): void {
    states.value = {}
    dismissed.value = false
    moveProblem.value = null
  }

  return {
    linked,
    stale,
    oldestReading,
    blockedOn,
    moveProblem,
    moveCsrdTo,
    showBar,
    dismissed,
    stateOf,
    check,
    checkAll,
    refresh,
    refreshAll,
    reset,
  }
})
